/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
// Import here Polyfills if needed. Recommended core-js (npm i -D core-js)
// import "core-js/fn/array.find"
// ...}

/**
 * @author Joel Hernandez <lifenautjoe@gmail.com>
 */

export class Droppable {
  private static ENTER_KEY_CODE = 13;

  private dragOverClass: string;

  private appendStatusClasses: boolean;
  private isClickable: boolean;

  private element: HTMLElement;

  private elementEventsRemover: Function;

  private virtualInputElement: HTMLInputElement;
  private virtualInputElementEventsRemover: Function;

  private elementKeyDownEventRemover: Function;

  private latestDroppedFiles: ValidatedFile[];

  private onFilesDroppedEventListeners: FilesWereDroppedEventListener[] = [];

  private acceptedFiles: string[];
  private maxFileSize: number;
  private maxFiles: number;

  constructor(private config: DroppableSettings) {}

  OnInit() {
    if (!this.config.element) {
      throw new Error('config.element: HTMLElement is required');
    }

    // This must be called before calling setAcceptsMultipleFiles
    this.virtualInputElement = Droppable.makeVirtualInputElement();

    const isClickable = typeof this.config.isClickable === 'boolean' ? this.config.isClickable : true;
    const acceptsMultipleFiles =
      typeof this.config.acceptsMultipleFiles === 'boolean' ? this.config.acceptsMultipleFiles : true;
    const appendStatusClasses =
      typeof this.config.appendStatusClasses === 'boolean' ? this.config.appendStatusClasses : true;

    this.dragOverClass = typeof this.config.dragOverClass === 'string' ? this.config.dragOverClass : 'dragover';
    this.element = this.config.element;

    this.setAcceptsMultipleFiles(acceptsMultipleFiles);
    this.setAppendStatusClasses(appendStatusClasses);
    this.setAcceptedFiles(this.config.acceptedFiles || []);
    this.setMaxFileSize(this.config.maxFileSize);
    this.setMaxFiles(this.config.maxFiles);
    this.setFilesCount(this.config.filesCount || 0);
    this.setMaxTotalFileSize(this.config.maxTotalFileSize);
    this.setFilesTotalSize(this.config.filesTotalSize || 0);

    this.elementEventsRemover = this.registerElementEvents();
    this.setIsClickable(isClickable);

    this.virtualInputElementEventsRemover = this.registerVirtualInputElementEvents();
  }

  setAcceptedFiles(files: string[]) {
    this.acceptedFiles = files;
    if (this.virtualInputElement && files.length > 0) {
      this.virtualInputElement.setAttribute('accept', files.join(','));
    } else if (this.virtualInputElement) {
      this.virtualInputElement.removeAttribute('accept');
    }
  }

  setMaxFileSize(size: number) {
    this.maxFileSize = size;
  }

  setMaxFiles(maxFiles: number) {
    this.maxFiles = maxFiles;
  }

  setFilesCount(filesCount: number) {
    this.filesCount = filesCount;
  }

  setMaxTotalFileSize(size: number) {
    this.maxTotalFileSize = size;
  }

  setFilesTotalSize(size: number) {
    this.filesTotalSize = size;
  }

  private filesCount = 0;
  private maxTotalFileSize: number;
  private filesTotalSize = 0;

  private static makeVirtualInputElement() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.style.display = 'none';
    return input;
  }

  private static addAccessibilityAttributesToDroppableElement(element: HTMLElement) {
    element.tabIndex = 0;
    element.role = 'button';
  }

  private static removeAccessibilityAttributesToDroppableElement(element: HTMLElement) {
    delete element?.role;
    element?.removeAttribute('tabIndex');
  }

  onFilesDropped(listener: FilesWereDroppedEventListener): EventRemover {
    this.onFilesDroppedEventListeners.push(listener);
    return () => {
      const listenerIndex = this.onFilesDroppedEventListeners.indexOf(listener);
      this.onFilesDroppedEventListeners.splice(listenerIndex, 1);
    };
  }

  destroy() {
    this.elementEventsRemover();
    this.virtualInputElementEventsRemover();
    this.onFilesDroppedEventListeners = [];
    Droppable.removeAccessibilityAttributesToDroppableElement(this.element);
  }

  getLatestDroppedFiles(): ValidatedFile[] {
    if (this.latestDroppedFiles) {
      return this.latestDroppedFiles;
    }
    return [];
  }

  promptForFiles(): void {
    this.virtualInputElement.click();
  }

  setIsClickable(clickable: boolean) {
    this.isClickable = clickable;
    if (clickable) {
      Droppable.addAccessibilityAttributesToDroppableElement(this.element);
    } else {
      Droppable.removeAccessibilityAttributesToDroppableElement(this.element);
    }
  }

  setAcceptsMultipleFiles(acceptsMultipleFiles: boolean) {
    if (acceptsMultipleFiles) this.virtualInputElement.setAttribute('multiple', '');
  }

  setAppendStatusClasses(appendStatusClasses: boolean) {
    this.appendStatusClasses = appendStatusClasses;
  }

  private registerElementEvents(): Function {
    const eventNameToEventListenerDictionary = this.getElementEventsDictionary();
    return this.registerElementEventsWithDictionary(this.element, eventNameToEventListenerDictionary);
  }

  private registerVirtualInputElementEvents(): Function {
    const eventNameToEventListenerDictionary = this.getVirtualInputElementEventsDictionary();
    return this.registerElementEventsWithDictionary(this.virtualInputElement, eventNameToEventListenerDictionary);
  }

  private getVirtualInputElementEventsDictionary() {
    return {
      change: this.onVirtualInputElementChange,
    };
  }

  private getElementEventsDictionary() {
    return {
      dragover: this.onElementDragOver,
      dragleave: this.onElementDragLeave,
      drop: this.onElementDrop,
      click: this.onElementClick,
      focus: this.onElementFocus,
      focusout: this.onElementFocusOut,
    };
  }

  private onElementDragOver(e: Event) {
    // console.log('Drag over');
    e.preventDefault();
    e.stopPropagation();

    if (this.appendStatusClasses) {
      this.element.classList.add(this.dragOverClass);
    }
  }

  private onElementDragLeave(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.element.classList.remove(this.dragOverClass);
  }

  private onElementDrop(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.element.classList.remove(this.dragOverClass);
    this.onDroppableElementChange(e);
  }

  private onElementClick() {
    if (this.isClickable) this.promptForFiles();
  }

  private onElementKeyDown(e: { [key: string]: any }) {
    if (e['keyCode'] === Droppable.ENTER_KEY_CODE) {
      this.promptForFiles();
      this.element.blur();
    }
  }

  private onElementFocus() {
    this.elementKeyDownEventRemover = this.registerElementEventsWithDictionary(this.element, {
      keydown: this.onElementKeyDown,
    });
  }

  private onElementFocusOut() {
    if (this.elementKeyDownEventRemover) this.elementKeyDownEventRemover();
  }

  private onVirtualInputElementChange(e: Event) {
    this.onDroppableElementChange(e);
    this.virtualInputElement.value = '';
  }

  private onDroppableElementChange(event: { [key: string]: any }) {
    let files;
    if (event['dataTransfer']) {
      files = event['dataTransfer'].files;
    } else if (event['target']) {
      files = event['target'].files;
    } else {
      throw Error('Fired event contains no files');
    }

    // Files is FileList, we convert to array
    const filesArray: File[] = Array.from(files);

    let accumulatedBatchSize = 0;

    const validatedFiles = filesArray.map((file, index) => {
      const validity: FileValidity = {
        valid: true,
        config: this.config,
      };

      // Check max files limit
      if (this.maxFiles && index + this.filesCount >= this.maxFiles) {
        validity.maxFilesReached = true;
        validity.valid = false;
        console.warn(
          `File ${file.name} ignored: max files limit of ${this.maxFiles} reached (current: ${this.filesCount}).`
        );
      }

      // Check file size
      if (this.maxFileSize && file.size > this.maxFileSize) {
        validity.maxFileSizeReached = true;
        validity.valid = false;
        console.warn(`File ${file.name} ignored: exceeds max size of ${this.maxFileSize} bytes.`);
      } else {
        // Only count size if individual file size is valid (or if we want to count even invalid ones? usually valid ones)
        // Let's count it for the batch check, but we only really care if it pushes us over.
        accumulatedBatchSize += file.size;

        if (this.maxTotalFileSize && this.filesTotalSize + accumulatedBatchSize > this.maxTotalFileSize) {
          validity.maxTotalFileSizeReached = true;
          validity.valid = false;
          console.warn(`File ${file.name} ignored: total size limit of ${this.maxTotalFileSize} reached.`);
        }
      }

      // Check accepted types
      if (this.acceptedFiles && this.acceptedFiles.length > 0) {
        const fileType = file.type;
        const fileName = file.name;

        // Check if file matches any accepted type/extension
        const isAccepted = this.acceptedFiles.some((accept) => {
          // 1. Exact mime type match (e.g. "image/png")
          if (accept === fileType) return true;

          // 2. Wildcard mime type match (e.g. "image/*")
          if (accept.endsWith('/*') && fileType.startsWith(accept.replace('/*', ''))) return true;

          // 3. Extension match (e.g. ".png")
          if (accept.startsWith('.') && fileName.toLowerCase().endsWith(accept.toLowerCase())) return true;

          return false;
        });

        if (!isAccepted) {
          validity.mimeTypeNotAccepted = true;
          validity.valid = false;
          console.warn(`File ${file.name} ignored: type ${fileType} not accepted.`);
        }
      }

      Object.defineProperty(file, 'validity', {
        value: validity,
        writable: true,
      });

      return file as ValidatedFile;
    });

    this.setLatestDrop(validatedFiles);
  }

  private setLatestDrop(files: ValidatedFile[]) {
    this.latestDroppedFiles = files;
    this.emitFilesWereDropped(files);
  }

  private emitFilesWereDropped(files: ValidatedFile[]) {
    this.onFilesDroppedEventListeners.forEach((listener) => {
      listener(files);
    });
  }

  private registerElementEventsWithDictionary(
    element: HTMLElement,
    eventNameToEventListenerDictionary: { [key: string]: EventListener }
  ): Function {
    const eventRemovers: Array<Function> = [];
    Object.keys(eventNameToEventListenerDictionary).forEach((eventName) => {
      const eventListener = eventNameToEventListenerDictionary[eventName];
      const boundEventListener = eventListener.bind(this);
      element.addEventListener(eventName, boundEventListener);
      eventRemovers.push(() => element.removeEventListener(eventName, boundEventListener));
    });

    return () => eventRemovers.forEach((eventRemover) => eventRemover());
  }
}

export interface DroppableSettings {
  element: HTMLElement;
  dragOverClass?: string;
  appendStatusClasses?: boolean;
  acceptsMultipleFiles?: boolean;
  isClickable?: boolean;
  acceptedFiles?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  filesCount?: number;
  maxTotalFileSize?: number;
  filesTotalSize?: number;
}

export type EventRemover = () => void;

export type FilesWereDroppedEventListener = (files: ValidatedFile[]) => any;

export interface FileValidity {
  maxFileSizeReached?: boolean;
  maxFilesReached?: boolean;
  maxTotalFileSizeReached?: boolean;
  mimeTypeNotAccepted?: boolean;
  valid: boolean;
  config: DroppableSettings;
}

export interface ValidatedFile extends File {
  validity: FileValidity;
}
